import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { dropOn, dropTargetAt, rememberDrop, setDragging } from './dropTargets';
import { DRAG, gestureIntent, scrollsSideways } from './gestureIntent';

/** How far a card moves before it lifts. */
const DRAG_START_PX = 6;
/** How long a dropped card waits where it was let go for the game to take it, before going back. */
const DROP_WAIT_MS = 2500;
/** The dragged card is drawn a little bigger than where it was, as if lifted. */
const LIFT = 'scale(1.06)';

/**
 * Where the lifted card is drawn: following the finger (dx, dy) plus `fix`, the gap between where the card was meant
 * to be and where the browser put it. Zoomed in on a phone, a fixed-position card and the finger's position can be
 * measured from different corners of the screen (the zoomed view versus the whole page), and the gap moves as the
 * zoomed view pans; measuring it keeps the card under the finger whatever the browser does.
 */
const placeGhost = (ghost, g) => {
  g.moved = { x: g.dx + g.fix.x, y: g.dy + g.fix.y };
  ghost.style.transform = `translate(${g.moved.x}px, ${g.moved.y}px) ${LIFT}`;
};

/**
 * Measures the gap (see placeGhost) and puts the card where it's meant to be: its center where the card's center
 * was, moved as far as the finger has. (Lifting scales it about its center, which leaves the center put.)
 */
const fixGhost = (ghost, g) => {
  const now = ghost.getBoundingClientRect();
  // Where the browser draws it before any moving.
  const drawnX = now.left + now.width / 2 - g.moved.x;
  const drawnY = now.top + now.height / 2 - g.moved.y;
  g.fix = { x: g.rect.left + g.rect.width / 2 - drawnX, y: g.rect.top + g.rect.height / 2 - drawnY };
  placeGhost(ghost, g);
};

/** Reads a touch's direction once it's far enough to tell, from whichever of its events arrives first. */
const decide = (g, x, y, timeStamp) => {
  if (g.claimed === undefined) {
    const intent = gestureIntent({ dx: x - g.x, dy: y - g.y, heldMs: timeStamp - g.at, handScrolls: g.handScrolls });
    g.claimed = intent === null ? undefined : intent === DRAG;
  }
};

/**
 * Dragging a card (from the hand, or a Pokémon in play) onto a lit spot on the board. A tap still taps: a click right
 * after a drag is swallowed, nothing else is.
 *
 * Starting a drag calls `lift`, which starts whatever flow lights the spots up and returns what's being dragged
 * (setDragging, which the spots read). Letting go on a lit spot does what tapping it would (dropTargetAt). The card
 * then waits where it was let go until the game takes it (it unmounts, or `settledBy` changes) so it doesn't fly
 * there again (rememberDrop); a spot whose tap asks something first returns false, and the card goes back at once.
 * Letting go anywhere else calls `onMiss`. While it's lifted, what was dragged is hidden from its place, unless it
 * `staysWhileLifted` (a card drawn off the deck leaves the deck there).
 *
 * The finger is followed outside React (the lifted card is moved directly, once a frame), and a touch is claimed in
 * its first few pixels (gestureIntent), so the phone's browser doesn't take it for a scroll.
 *
 * @returns {{ bind: object, ghost: function }} props for the element that's dragged, and a function that draws the
 *   lifted card (given what the element shows) while it's lifted
 */
export default function useCardDrag({
  enabled, place, lift, onMiss, settledBy, touchAction = 'none', staysWhileLifted = false,
}) {
  const ref = useRef(null);
  const ghostRef = useRef(null);
  const gesture = useRef(null);
  const suppressClick = useRef(false);
  const latest = useRef(null);
  latest.current = { place, lift, onMiss };
  // Where the lifted card starts (the card's place on screen); null when nothing is lifted.
  const [lifted, setLifted] = useState(null);
  const [waitingForGame, setWaitingForGame] = useState(false);

  useEffect(() => {
    if (!waitingForGame) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setWaitingForGame(false);
      setLifted(null);
    }, DROP_WAIT_MS);
    return () => clearTimeout(timer);
  }, [waitingForGame]);

  // The game answered a drop: what was dragged is somewhere else now. (Not mid-drag: an update from the opponent
  // mustn't take the card out from under the finger.)
  const waitingRef = useRef(false);
  waitingRef.current = waitingForGame;
  useEffect(() => {
    if (waitingRef.current) {
      setWaitingForGame(false);
      setLifted(null);
    }
  }, [settledBy]);

  // The lifted card, once drawn, is measured and put under the finger, and again whenever the zoomed-in view pans or
  // zooms while it's held (placeGhost).
  useLayoutEffect(() => {
    const g = gesture.current;
    if (!lifted || !g || !ghostRef.current) {
      return undefined;
    }
    fixGhost(ghostRef.current, g);
    const viewport = window.visualViewport;
    if (!viewport) {
      return undefined;
    }
    const refit = () => {
      if (gesture.current === g && ghostRef.current) {
        fixGhost(ghostRef.current, g);
      }
    };
    viewport.addEventListener('scroll', refit);
    viewport.addEventListener('resize', refit);
    return () => {
      viewport.removeEventListener('scroll', refit);
      viewport.removeEventListener('resize', refit);
    };
  }, [lifted]);

  // Touch: once the card is claimed, nothing scrolls until the finger lifts.
  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return undefined;
    }
    const touchMove = (event) => {
      const g = gesture.current;
      const touch = event.touches[0];
      if (!g || !touch) {
        return;
      }
      decide(g, touch.clientX, touch.clientY, event.timeStamp);
      if (g.claimed && event.cancelable) {
        event.preventDefault();
      }
    };
    element.addEventListener('touchmove', touchMove, { passive: false });
    return () => element.removeEventListener('touchmove', touchMove);
  }, [enabled]);

  useEffect(() => {
    const follow = () => {
      const g = gesture.current;
      if (g) {
        g.frame = null;
        if (ghostRef.current) {
          placeGhost(ghostRef.current, g);
        }
      }
    };

    const move = (event) => {
      const g = gesture.current;
      if (!g || g.id !== event.pointerId) {
        return;
      }
      g.dx = event.clientX - g.x;
      g.dy = event.clientY - g.y;

      if (!g.dragging) {
        decide(g, event.clientX, event.clientY, event.timeStamp);
        if (g.claimed === false) {
          gesture.current = null; // scrolling through the hand
          return;
        }
        if (!g.claimed || Math.hypot(g.dx, g.dy) <= DRAG_START_PX) {
          return;
        }
        g.dragging = true;
        suppressClick.current = true;
        setLifted(g.rect);
        setDragging(latest.current.lift());
      }
      if (g.frame === null) {
        g.frame = requestAnimationFrame(follow);
      }
    };

    const end = (event, cancelled) => {
      const g = gesture.current;
      if (!g || g.id !== event.pointerId) {
        return;
      }
      gesture.current = null;
      if (!g.dragging) {
        return;
      }
      cancelAnimationFrame(g.frame);
      // The click that follows a drag comes right after this, if at all (not when it ended over something else),
      // so the next real tap isn't swallowed.
      setTimeout(() => { suppressClick.current = false; }, 0);

      // The innermost place under the finger that takes the card: dropping on a card in your hand is dropping on
      // your hand.
      const under = cancelled ? null : document.elementFromPoint(event.clientX, event.clientY);
      let anchor = under && under.closest('[data-anchor]');
      let commit = null;
      while (anchor && !(commit = dropTargetAt(anchor.dataset.anchor))) {
        anchor = anchor.parentElement && anchor.parentElement.closest('[data-anchor]');
      }
      setDragging(null);

      if (!commit) {
        setLifted(null);
        if (latest.current.onMiss) {
          latest.current.onMiss();
        }
        return;
      }
      if (ghostRef.current) {
        placeGhost(ghostRef.current, g);
      }
      if (dropOn(commit) === false) {
        setLifted(null); // it asks something first (a menu): the card goes back while you answer
        return;
      }
      rememberDrop(latest.current.place);
      setWaitingForGame(true);
    };

    const up = (event) => end(event, false);
    const cancel = (event) => end(event, true);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
    };
  }, []);

  const onPointerDown = (event) => {
    if (!enabled || (event.pointerType === 'mouse' && event.button !== 0)) {
      return;
    }
    gesture.current = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      dx: 0,
      dy: 0,
      at: event.timeStamp,
      rect: event.currentTarget.getBoundingClientRect(),
      // A mouse drags in every direction; a finger's first few pixels decide (decide).
      claimed: event.pointerType === 'mouse' ? true : undefined,
      handScrolls: scrollsSideways(event.currentTarget),
      dragging: false,
      frame: null,
      // The gap measured between where the lifted card is meant to be and where it's drawn, and how far it's been
      // moved so far (placeGhost); a newly drawn card hasn't been.
      fix: { x: 0, y: 0 },
      moved: { x: 0, y: 0 },
    };
  };

  const onClickCapture = (event) => {
    if (suppressClick.current) {
      suppressClick.current = false;
      event.stopPropagation();
      event.preventDefault();
    }
  };

  const bind = {
    ref,
    onPointerDown,
    onClickCapture,
    // Holding a card mustn't select text or bring up the iPhone's callout.
    style: {
      touchAction: enabled ? touchAction : undefined,
      userSelect: 'none',
      WebkitUserSelect: 'none',
      WebkitTouchCallout: 'none',
      visibility: lifted && !staysWhileLifted ? 'hidden' : undefined,
    },
  };

  const ghost = (face) => lifted && createPortal(
    <div
      ref={ghostRef}
      style={{
        position: 'fixed',
        left: lifted.left,
        top: lifted.top,
        zIndex: 41,
        pointerEvents: 'none',
        willChange: 'transform',
        transform: LIFT,
        '--card-w': `${lifted.width}px`,
        filter: 'drop-shadow(0 8px 14px rgba(0, 0, 0, .45))',
      }}
      aria-hidden="true"
    >
      {face}
    </div>,
    document.body,
  );

  return { bind, ghost };
}
